import React, { useState, useEffect } from 'react';
import { FiBarChart2, FiInfo } from 'react-icons/fi';
import api from '../../utils/api';
import * as S from '../../styles/inlineStyles';
import SubmissionReviewModal from '../../components/student/SubmissionReviewModal';

const StudentResults = () => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [viewingDetails, setViewingDetails] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => { fetchResults(); }, []);

    const fetchResults = async () => {
        try { 
            const [resultRes, examRes] = await Promise.all([
                api.get('/api/student/results'),
                api.get('/api/student/upcoming-exams')
            ]);
            const marks = resultRes.data.results || [];
            const exams = examRes.data.exams || [];

            // Combine actual results with synthesized ones for ALL "Completed" exams (Submitted or Ended)
            const combined = [...marks];
            exams.forEach(e => {
                const isCompleted = e.alreadySubmitted || e.state === 'ENDED';
                const alreadyInResults = marks.some(m => m.paper_id === e.paperId || (m.subject_code === e.subject_code && m.id === e.id));
                
                if (isCompleted && !alreadyInResults) {
                    combined.push({
                        id: `synth-${e.id}`,
                        paper_id: e.paperId,
                        title: e.subject_name,
                        subject_name: e.subject_name,
                        subject_code: e.subject_code,
                        total_marks: e.total_marks,
                        submitted_at: e.state === 'ENDED' ? e.endTime : new Date().toISOString(),
                        graded_at: null,
                        status: e.alreadySubmitted ? 'Completed' : 'Missed'
                    });
                }
            });
            setResults(combined);
        } catch (err) { 
            console.error('Failed to fetch results'); 
        } finally { 
            setLoading(false); 
        }
    };

    const handleViewDetails = async (subId) => {
        if (!subId || subId.toString().startsWith('synth-')) {
            alert("Submission data not found for this entry.");
            return;
        }
        setDetailLoading(true);
        try {
            const res = await api.get(`/api/student/submissions/${subId}/details`);
            setSelectedSubmission(res.data);
            setViewingDetails(true);
        } catch (err) {
            console.error('Failed to fetch submission details');
            alert("Failed to load details. It's possible the data is still being processed.");
        } finally {
            setDetailLoading(false);
        }
    };

    if (loading) return <div style={S.loadingContainer}><div style={S.spinner} /><p style={S.loadingText}>Loading results...</p></div>;

    return (
        <div className="page-enter" style={{ paddingBottom: 40 }}>
            <div style={S.pageHeader}>
                <h1 style={S.pageTitle}>📊 My Results</h1>
                <p style={S.pageSubtitle}>View your examination results and scores. Click a card to view details.</p>
            </div>

            {results.length === 0 ? (
                <div style={S.card}><div style={S.emptyState}><span style={{ ...S.emptyIcon, fontSize: 48 }}><FiBarChart2 /></span><h3 style={S.emptyTitle}>No Results Yet</h3><p style={S.emptyText}>Your exam results will appear here once evaluated.</p></div></div>
            ) : (
                <div style={S.grid3} className="responsive-grid">
                    {results.map(r => {
                        const maxMarks = r.total_marks || 0;
                        const percentage = maxMarks > 0 ? Math.round((r.total_score / maxMarks) * 100) : 0;
                        const isMissed = r.status === 'Missed';
                        const isSynth = r.id.toString().startsWith('synth-');

                        return (
                            <div key={r.id} onClick={() => !isMissed && !isSynth && handleViewDetails(r.id)} style={{
                                ...S.card, 
                                cursor: isMissed || isSynth ? 'default' : 'pointer',
                                opacity: isMissed ? 0.7 : 1,
                                border: selectedSubmission?.submission?.id === r.id ? `2px solid ${S.colors.primary}` : S.card.border
                            }} className={isMissed || isSynth ? "" : "card-hover"}>
                                <div style={S.cardBody}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                        <h3 style={{ fontFamily: S.font, fontSize: 16, fontWeight: 700, margin: 0, color: S.colors.textPrimary }}>{r.title || 'Exam'}</h3>
                                        {!isMissed && !isSynth && <FiInfo color={S.colors.textLight} size={14} />}
                                    </div>
                                    <div style={{ fontSize: 13, color: S.colors.primary, fontWeight: 600, marginBottom: 16 }}>{r.subject_name || 'Subject'} ({r.subject_code})</div>
                                    <div style={{ textAlign: 'center', padding: '16px 0', borderTop: `1px solid ${S.colors.border}`, borderBottom: `1px solid ${S.colors.border}`, marginBottom: 12 }}>
                                        {isMissed ? (
                                            <div style={{ fontSize: 24, fontWeight: 800, color: '#ef4444', fontFamily: S.font, padding: '12px 0' }}>MISSED</div>
                                        ) : !r.graded_at && !isSynth ? (
                                            <div style={{ fontSize: 20, fontWeight: 700, color: S.colors.warning, fontFamily: S.font, padding: '12px 0' }}>Pending Evaluation</div>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: 36, fontWeight: 800, color: percentage >= 50 ? S.colors.success : S.colors.danger, fontFamily: S.font }}>{percentage}%</div>
                                                <div style={{ fontSize: 13, color: S.colors.textSecondary, marginTop: 4 }}>{r.total_score} / {maxMarks} marks</div>
                                            </>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: S.colors.textLight }}>
                                        <span>{isMissed ? 'Not Attempted' : r.graded_at ? 'Evaluated' : 'Submitted'}</span>
                                        <span>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : ''}</span>
                                    </div>
                                    {!isMissed && !isSynth && (
                                        <div style={{ marginTop: 12, fontSize: 11, color: S.colors.primary, fontWeight: 700, textAlign: 'center' }}>
                                            View Detailed Submission →
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Reusable Submission Review Modal */}
            <SubmissionReviewModal 
                isOpen={viewingDetails} 
                onClose={() => setViewingDetails(false)} 
                data={selectedSubmission}
                apiBaseUrl={api.defaults.baseURL}
            />

            {detailLoading && (
                <div style={{ ...S.confirmOverlay, background: 'rgba(255,255,255,0.7)', zIndex: 2000 }}>
                    <div style={S.spinner} />
                    <p style={{ marginTop: 16, fontWeight: 700, color: S.colors.primary }}>Fetching submission details...</p>
                </div>
            )}
        </div>
    );
};

export default StudentResults;
